import React, { useState } from 'react';
import { X } from 'lucide-react';

const DISMISS_KEY = 'te.firstrun.dismissed';

const STEPS: { label: string; detail: string; href?: string }[] = [
  { label: 'Watch the movers', detail: 'These panels are live — click any symbol to open its chart and technicals.' },
  { label: 'Place a simulated paper trade', detail: 'No broker needed — every account starts with a $100k simulated balance.', href: '#/dashboard/paper-trading' },
  { label: 'Turn a screener into a trading rule', detail: 'Pick a scanner, set sizing and exits — it paper-trades every hit.', href: '#/dashboard/automation' },
  { label: 'Backtest it over history', detail: 'Prove the rule before trusting it with real money.', href: '#/dashboard/scenarios' },
];

/**
 * The onboarding the go-live audit flagged as missing, in its cheapest honest
 * form: a dismissible four-step tour of the core loop, shown on the landing tab
 * until dismissed. Per-browser (localStorage) — a convenience, not state worth
 * syncing. The AI assistant can execute every step, so it gets a plug too.
 */
export const FirstRunChecklist: React.FC = () => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return true; }
  });
  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold mb-1.5">New here? The whole loop in four steps:</div>
          <ol className="grid gap-x-6 gap-y-1 sm:grid-cols-2 text-xs text-muted-foreground list-none m-0 p-0">
            {STEPS.map((s, i) => (
              <li key={s.label} className="flex items-start gap-1.5">
                <span className="font-mono text-primary font-semibold shrink-0">{i + 1}.</span>
                <span>
                  {s.href
                    ? <a href={s.href} className="text-foreground font-medium underline decoration-primary/50 hover:decoration-primary">{s.label}</a>
                    : <span className="text-foreground font-medium">{s.label}</span>}
                  {' — '}{s.detail}
                </span>
              </li>
            ))}
          </ol>
          <div className="text-xs text-muted-foreground mt-1.5">
            Prefer to delegate? The <a href="#/dashboard/ai-assistant" className="text-primary underline">AI assistant</a> can
            do all four from a chat.
          </div>
        </div>
        <button onClick={dismiss} aria-label="Dismiss checklist"
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0"
          title="Dismiss — this won't show again on this browser">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default FirstRunChecklist;
